import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, Eye, EyeOff, Sparkles } from "lucide-react";
import { loginService } from "../services";
import { LoginContext } from "../context/LoginContext";


export default function Main() {

    const navigate = useNavigate();
    const { email, setEmail } = useContext(LoginContext);
    const { password, setPassword } = useContext(LoginContext);

    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const logged = await loginService(email, password);
            if (logged) {
                setTimeout(() => navigate("/"), 1500);
            }
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="relative min-h-screen w-full overflow-hidden">

            {/* Atmosphere — animated glow blobs */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[640px] w-[640px] rounded-full bg-brand-500/20 blur-[120px] animate-glow-pulse" />
                <div className="absolute -bottom-32 -right-20 h-[480px] w-[480px] rounded-full bg-accent-violet/15 blur-[120px]" />
                <div className="absolute top-1/3 -left-20 h-[380px] w-[380px] rounded-full bg-brand-700/15 blur-[100px]" />
            </div>

            {/* Mesh grid overlay */}
            <div className="pointer-events-none absolute inset-0 -z-10 mesh-bg opacity-40" />

            <div className="grid min-h-screen lg:grid-cols-2">

                {/* LEFT — Brand panel (hidden on small screens) */}
                <aside className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16 border-r border-white/[0.04]">

                    {/* Logo top */}
                    <div className="flex items-center gap-3 animate-fade-in">
                        <div className="relative">
                            <div className="h-10 w-10 rounded-xl bg-gradient-brand shadow-glow grid place-items-center">
                                <Sparkles className="h-5 w-5 text-white" strokeWidth={2.5} />
                            </div>
                            <div className="absolute inset-0 rounded-xl bg-brand-500/40 blur-xl -z-10" />
                        </div>
                        <span className="font-display font-bold text-2xl tracking-tight">Metriqa</span>
                    </div>

                    {/* Headline */}
                    <div className="space-y-8 animate-slide-up">
                        <div className="space-y-4">
                            <span className="chip-brand">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-400 animate-glow-pulse" />
                                Plataforma de performance
                            </span>
                            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
                                <span className="text-gradient-brand">Inteligência</span>
                                <br />
                                de vendas em
                                <br />
                                tempo real.
                            </h1>
                            <p className="text-ink-3 text-lg max-w-md leading-relaxed">
                                Tracking, ROI, automações de WhatsApp e agentes de IA — tudo em uma plataforma só.
                            </p>
                        </div>

                        {/* Mini stats bento */}
                        <div className="grid grid-cols-3 gap-3 max-w-md">
                            {[
                                { label: "Vendas/dia", value: "2.4k+" },
                                { label: "Empresas", value: "180+" },
                                { label: "Uptime", value: "99.9%" },
                            ].map((stat) => (
                                <div key={stat.label} className="surface-card p-4">
                                    <div className="font-display text-xl font-bold text-ink-1">{stat.value}</div>
                                    <div className="label-overline mt-1">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <p className="text-ink-4 text-sm">
                        © 2026 Metriqa. Todos os direitos reservados.
                    </p>
                </aside>

                {/* RIGHT — Form */}
                <main className="flex items-center justify-center p-6 sm:p-12">
                    <div className="w-full max-w-md animate-slide-up">

                        {/* Logo on mobile */}
                        <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
                            <div className="h-10 w-10 rounded-xl bg-gradient-brand shadow-glow grid place-items-center">
                                <Sparkles className="h-5 w-5 text-white" strokeWidth={2.5} />
                            </div>
                            <span className="font-display font-bold text-2xl tracking-tight">Metriqa</span>
                        </div>

                        {/* Form card */}
                        <div className="glow-card p-8 sm:p-10">

                            <div className="mb-8">
                                <h2 className="heading-1 mb-2">Bem-vindo de volta</h2>
                                <p className="text-ink-3 text-sm">
                                    Entre com seu e-mail e senha para acessar o painel.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">

                                {/* Email */}
                                <div className="space-y-2">
                                    <label htmlFor="email" className="label-overline block">E-mail</label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4 pointer-events-none" />
                                        <input
                                            id="email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            autoComplete="email"
                                            className="input-field pl-11"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="password" className="label-overline">Senha</label>
                                        <button type="button" className="text-xs text-brand-300 hover:text-brand-200 transition-colors">
                                            Esqueceu?
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4 pointer-events-none" />
                                        <input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            autoComplete="current-password"
                                            className="input-field pl-11 pr-11"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(v => !v)}
                                            tabIndex={-1}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-ink-4 hover:text-ink-2 hover:bg-white/5 transition"
                                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                        >
                                            {showPassword
                                                ? <EyeOff className="h-4 w-4" />
                                                : <Eye className="h-4 w-4" />
                                            }
                                        </button>
                                    </div>
                                </div>

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary w-full mt-2"
                                >
                                    {submitting ? (
                                        <>
                                            <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                            <span>Entrando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Entrar no painel</span>
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Divider */}
                            <div className="my-8 divider" />

                            {/* Register link */}
                            <p className="text-center text-sm text-ink-3">
                                Ainda não tem conta?{" "}
                                <button
                                    onClick={() => navigate("/register")}
                                    className="text-brand-300 hover:text-brand-200 font-medium transition-colors"
                                >
                                    Cadastre-se
                                </button>
                            </p>
                        </div>

                        {/* Mobile footer */}
                        <p className="lg:hidden text-center text-ink-5 text-xs mt-8">
                            © 2026 Metriqa
                        </p>
                    </div>
                </main>
            </div>
        </div>
    );
}
